import { useState, useEffect, useRef } from 'react';

export default function Pricing() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openCart = () => {
    // OPRAVENO: Přímo volat globální funkci
    if (typeof (window as any).openQuickCart === 'function') {
      console.log('🛒 Volám globální funkci openQuickCart()');
      (window as any).openQuickCart();
    } else {
      // Fallback: Zkusit event
      console.log('🛒 Globální funkce neexistuje, odesílám event');
      window.dispatchEvent(new Event('openQuickCart'));
    }
  };

  const membershipPlans = [
    {
      name: 'BASIC',
      subtitle: 'Comfort Care',
      price: '599',
      pieces: '10',
      discount: '15',
      deliveryTime: '72 h',
      shipping: '399 Kč',
      icon: 'ri-star-line',
      features: [
        'Garantovaná cena za 10 kusů',
        '−15 % sleva na služby',
        'Dodání: 72 h',
        'Doprava: 399 Kč'
      ],
      examples: [
        { name: 'Košile', regular: 79, member: 67 },
        { name: 'Kalhoty', regular: 75, member: 64 },
        { name: 'Tričko', regular: 65, member: 55 }
      ],
      popular: false
    },
    {
      name: 'PREMIUM',
      subtitle: 'Executive Care',
      price: '999',
      pieces: '20',
      discount: '20',
      deliveryTime: '48 h',
      shipping: '399 Kč',
      icon: 'ri-vip-crown-line',
      features: [
        'Garantovaná cena za 20 kusů',
        '−20 % sleva na služby',
        'Dodání: 48 h (pracovní dny)',
        'Doprava: 399 Kč'
      ],
      examples: [
        { name: 'Košile', regular: 79, member: 63 },
        { name: 'Kalhoty', regular: 75, member: 60 },
        { name: 'Tričko', regular: 65, member: 52 }
      ],
      popular: true
    },
    {
      name: 'VIP',
      subtitle: 'Váš čas je dražší než žehlička.',
      price: '1799',
      pieces: '30',
      discount: '30',
      deliveryTime: '24 h',
      shipping: 'ZDARMA',
      icon: 'ri-vip-diamond-line',
      features: [
        'Garantovaná cena za 30 kusů',
        '−30 % sleva na služby',
        'Dodání: 24 h (i o víkendu)',
        'Doprava ZDARMA',
        'VIP podpora'
      ],
      examples: [
        { name: 'Košile', regular: 79, member: 55 },
        { name: 'Kalhoty', regular: 75, member: 53 },
        { name: 'Tričko', regular: 65, member: 46 }
      ],
      popular: false
    }
  ];

  return (
    <section ref={sectionRef} id="cenik" className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Hlavička */}
        <div
          className={`text-center mb-12 sm:mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="inline-block text-sm font-medium text-teal-600 dark:text-teal-400 mb-2">
            Ušetřete až 30% s členstvím
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Členství, které se vyplatí.
          </h2>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Pravidelně žehlíte? Ušetřete čas i peníze s našimi výhodnými balíčky.
          </p>
        </div>

        {/* CENÍK */}
        <div
          className={`mb-12 sm:mb-16 transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <div className="text-center mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Ceník
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Transparentní ceny za jednotlivé služby
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {/* Košile a halenky */}
              <div className="space-y-2 sm:space-y-3">
                <h4 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide flex items-center gap-2">
                  <i className="ri-shirt-line text-teal-600"></i>
                  <span className="hidden sm:inline">Košile a halenky</span>
                  <span className="sm:hidden">Košile</span>
                </h4>
                <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Košile</span>
                    <span className="font-medium text-gray-900 dark:text-white">79 Kč</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Halenka</span>
                    <span className="font-medium text-gray-900 dark:text-white">75 Kč</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Polo</span>
                    <span className="font-medium text-gray-900 dark:text-white">65 Kč</span>
                  </div>
                </div>
              </div>

              {/* Kalhoty a sukně */}
              <div className="space-y-2 sm:space-y-3">
                <h4 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide flex items-center gap-2">
                  <i className="ri-user-line text-teal-600"></i>
                  <span className="hidden sm:inline">Kalhoty a sukně</span>
                  <span className="sm:hidden">Kalhoty</span>
                </h4>
                <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Kalhoty</span>
                    <span className="font-medium text-gray-900 dark:text-white">75 Kč</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Džíny</span>
                    <span className="font-medium text-gray-900 dark:text-white">69 Kč</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Sukně</span>
                    <span className="font-medium text-gray-900 dark:text-white">75 Kč</span>
                  </div>
                </div>
              </div>

              {/* Šaty a saka */}
              <div className="space-y-2 sm:space-y-3">
                <h4 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide flex items-center gap-2">
                  <i className="ri-women-line text-teal-600"></i>
                  <span className="hidden sm:inline">Šaty a saka</span>
                  <span className="sm:hidden">Šaty</span>
                </h4>
                <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Šaty</span>
                    <span className="font-medium text-gray-900 dark:text-white">95 Kč</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Složité</span>
                    <span className="font-medium text-gray-900 dark:text-white">125 Kč</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Sako</span>
                    <span className="font-medium text-gray-900 dark:text-white">95 Kč</span>
                  </div>
                </div>
              </div>

              {/* Ložní prádlo */}
              <div className="space-y-2 sm:space-y-3">
                <h4 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide flex items-center gap-2">
                  <i className="ri-hotel-bed-line text-teal-600"></i>
                  <span className="hidden sm:inline">Ložní prádlo</span>
                  <span className="sm:hidden">Prádlo</span>
                </h4>
                <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Polštář</span>
                    <span className="font-medium text-gray-900 dark:text-white">35 Kč</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Peřina</span>
                    <span className="font-medium text-gray-900 dark:text-white">69 Kč</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Prostěr.</span>
                    <span className="font-medium text-gray-900 dark:text-white">69 Kč</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA pro členství */}
            <div className="mt-6 sm:mt-8 pt-6 border-t border-gray-100 dark:border-gray-700 text-center">
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                Chcete ušetřit až 30%? Vyberte si členský plán níže.
              </p>
            </div>
          </div>
        </div>

        {/* Tarify členství */}
        <div
          className={`mb-12 sm:mb-16 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-8 sm:mb-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Členské plány
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Vyberte si plán, který vám vyhovuje
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {membershipPlans.map((plan, index) => (
              <div
                key={plan.name}
                className={`relative transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${300 + index * 100}ms` }}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-teal-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                      Nejoblíbenější
                    </span>
                  </div>
                )}
                
                <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-sm border h-full flex flex-col ${
                  plan.popular 
                    ? 'border-teal-500 ring-1 ring-teal-500' 
                    : 'border-gray-100 dark:border-gray-700'
                }`}>
                  {/* Hlavička tarifu */}
                  <div className="p-5 sm:p-6 text-center border-b border-gray-100 dark:border-gray-700">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <i className={`${plan.icon} text-lg sm:text-xl text-teal-600 dark:text-teal-400`}></i>
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">{plan.subtitle}</p>
                    <div className="mt-3 sm:mt-4">
                      <span className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{plan.price}</span>
                      <span className="text-gray-500 dark:text-gray-400 text-sm"> Kč</span>
                    </div>
                    <div className="mt-2 inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                      <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">za {plan.pieces} kusů</span>
                      <span className="text-xs sm:text-sm font-medium text-teal-600 dark:text-teal-400">−{plan.discount}%</span>
                    </div>
                  </div>

                  {/* Příklady cen */}
                  <div className="px-5 sm:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-gray-700/30">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">Příklady cen:</p>
                    <div className="space-y-1">
                      {plan.examples.map((ex) => (
                        <div key={ex.name} className="flex items-center justify-between text-xs sm:text-sm">
                          <span className="text-gray-600 dark:text-gray-400">{ex.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 line-through text-xs">{ex.regular} Kč</span>
                            <span className="font-medium text-gray-900 dark:text-white">{ex.member} Kč</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Výhody */}
                  <div className="p-5 sm:p-6 flex-1">
                    <ul className="space-y-2 sm:space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 sm:gap-3">
                          <i className="ri-check-line text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5"></i>
                          <span className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Dodání a doprava */}
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6">
                    <div className="flex items-center justify-between text-xs sm:text-sm bg-gray-50 dark:bg-gray-700/30 rounded-lg p-2 sm:p-3">
                      <div className="flex items-center gap-2">
                        <i className="ri-time-line text-gray-400"></i>
                        <span className="text-gray-600 dark:text-gray-400">{plan.deliveryTime}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-truck-line text-gray-400"></i>
                        <span className={`font-medium ${plan.shipping === 'ZDARMA' ? 'text-teal-600 dark:text-teal-400' : 'text-gray-600 dark:text-gray-400'}`}>
                          {plan.shipping}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Doprava */}
        <div
          className={`mb-12 sm:mb-16 transition-all duration-700 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 text-center">
              Ceny dopravy
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-2xl mx-auto">
              {/* Bez členství */}
              <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-4 sm:p-5">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <i className="ri-user-line text-gray-400"></i>
                  Bez členství
                </h4>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Všechny lokality</span>
                  <span className="font-bold text-lg sm:text-xl text-gray-900 dark:text-white">399 Kč</span>
                </div>
              </div>

              {/* S členstvím */}
              <div className="border border-teal-200 dark:border-teal-700 bg-teal-50/50 dark:bg-teal-900/10 rounded-xl p-4 sm:p-5">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <i className="ri-vip-crown-line text-teal-600"></i>
                  S členstvím
                </h4>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">BASIC / PREMIUM</span>
                    <span className="font-medium text-gray-900 dark:text-white">399 Kč</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">VIP</span>
                    <span className="font-medium text-teal-600 dark:text-teal-400">ZDARMA</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-center text-gray-500 dark:text-gray-400 text-xs mt-4">
              Cena zahrnuje svoz i rozvoz (2x cesta tam a zpět)
            </p>
          </div>
        </div>

        {/* Ramínka a obaly */}
        <div
          className={`transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <div className="text-center mb-6 sm:mb-8">
              <span className="inline-flex items-center gap-2 text-sm text-teal-600 dark:text-teal-400 mb-2">
                <i className="ri-leaf-line"></i>
                Myslíme na planetu
              </span>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                Ramínka a obaly
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 max-w-3xl mx-auto">
              {/* Ramínka */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <i className="ri-hanger-line text-teal-600"></i>
                  Ramínka
                </h4>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="flex items-center justify-between p-2.5 sm:p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300">Vlastní ramínka</span>
                    <span className="font-medium text-teal-600 dark:text-teal-400">ZDARMA</span>
                  </div>
                  <div className="flex items-center justify-between p-2.5 sm:p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <span className="text-gray-600 dark:text-gray-400">Plastové ramínko</span>
                    <span className="font-medium text-gray-900 dark:text-white">5 Kč/ks</span>
                  </div>
                </div>
              </div>

              {/* Obaly */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <i className="ri-shopping-bag-3-line text-teal-600"></i>
                  Obaly
                </h4>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="flex items-center justify-between p-2.5 sm:p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300">Vlastní látkový vak</span>
                    <span className="font-medium text-teal-600 dark:text-teal-400">ZDARMA</span>
                  </div>
                  <div className="flex items-center justify-between p-2.5 sm:p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <span className="text-gray-600 dark:text-gray-400">Igelitový obal</span>
                    <span className="font-medium text-gray-900 dark:text-white">15 Kč/ks</span>
                  </div>
                  <div className="flex items-center justify-between p-2.5 sm:p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <span className="text-gray-600 dark:text-gray-400">Oděvní vak</span>
                    <span className="font-medium text-gray-900 dark:text-white">89 Kč/ks</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-center text-gray-500 dark:text-gray-400 text-xs sm:text-sm mt-6">
              <i className="ri-leaf-line mr-1"></i>
              Přineste si vlastní ramínka a vak - společně chráníme planetu!
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
